/**
 * User Media Service
 * 
 * Data layer service for user media operations.
 * 
 * Singleton pattern for consistent state and efficient resource usage.
 * Matches the architecture of UserService and other service singletons.
 * 
 * Architecture:
 * UseCase → UserMediaService → FirestoreService
 */

import { firestoreService } from '../../../data/services/firestoreService';
import { cloudStorageService } from '@/data/storage/remote/cloudStorageService';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { UserMediaRemapper } from '@/domain/remappers/userMedia/userMediaRemapper';
import { TranscodeUserContentRequestRemapper } from '@/domain/remappers/userMedia/transcodeUserContentRequestRemapper';
import { buildUploadFileInfo } from '@/domain/utils/uploadFileInfoBuilder';
import { appLogger } from '@/domain/services/analytics/appLogger';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { PublishContentRequest } from '@/domain/entities/publish/publishContentRequest';
import type { Unsubscribe } from 'firebase/firestore';
import type { JsonObject, PageCursor } from '@/data/types/json';
import { RemoteDocumentChangeType } from '@/data/types/remoteDocumentChangeType';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';

export interface PublishContentResult {
  success: boolean;
}

export interface PublishContentOptions {
  onProgress?: (percent: number) => void;
}

export class UserMediaService {
  private static instance: UserMediaService;

  private constructor() { }

  public static getInstance(): UserMediaService {
    if (!UserMediaService.instance) {
      UserMediaService.instance = new UserMediaService();
    }
    return UserMediaService.instance;
  }

  /**
   * Get user's medias with pagination
   * Queries Firestore users/{userId}/media collection
   * 
   * @param userId - Firebase user ID to fetch medias for
   * @param currentUserId - Current authenticated user ID (for filtering)
   * @param cursor - Pagination cursor
   * @param limitCount - Number of items to fetch
   * @returns Paginated user media items
   */
  async getUserMedias(
    userId: string,
    currentUserId: string,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<{ items: JsonObject[]; hasMore: boolean; cursor?: PageCursor }> {
    // console.log('[UserMediaService] Fetching user medias');

    const result = await firestoreService.getUserMedias(
      userId,
      currentUserId,
      limitCount,
      cursor,
    );

    // console.log('[UserMediaService] Fetched user medias');

    return result;
  }

  /**
   * Upload a user video and trigger the transcode cloud function.
   * Mirrors Flutter's AppStorageService._uploadUserVideoAndTriggerTranscode
   * (lib/services/app_storage_service.dart:68): storage upload, then cloud
   * function call fired-and-forgotten. The promise resolves once the upload
   * finishes — success indicates upload-only. Transcode runs in the background;
   * any failure is reflected on the media item itself (no UI callback).
   */
  async publishContent(
    request: PublishContentRequest,
    { onProgress }: PublishContentOptions = {},
  ): Promise<PublishContentResult> {
    const uploadInfo = buildUploadFileInfo({
      file: request.file,
      firebaseUserId: request.firebaseUserId,
      durationSeconds: request.videoMeta.duration,
    });
    const uploadPath = MediaUtils.getUserContentUploadFilePath(uploadInfo);

    let downloadUrl: string;
    try {
      const result = await cloudStorageService.uploadUserMediaContent(
        uploadInfo.file,
        uploadPath,
        uploadInfo.contentType,
        { onProgress },
      );
      downloadUrl = result.downloadUrl;
    } catch (error) {
      appLogger.error('Failed to upload user media', 'UserMediaService.publishContent', { error: error as Error });
      return { success: false };
    }

    const payload = UserMediaRemapper.toTranscodeRequest(request, uploadInfo, downloadUrl, uploadPath);
    void cloudFunctionsService.transcodeUserContent(TranscodeUserContentRequestRemapper.toJson(payload)).catch((error) => {
      appLogger.error('Transcode cloud function failed', 'UserMediaService.publishContent', { error: error as Error });
    });

    return { success: true };
  }

  /**
   * Subscribe to live Firestore changes of a user media by id. Remaps each
   * doc change via UserMediaRemapper. Mirrors Flutter's
   * user_details_screen.dart:_listenToMediaLiveChanges.
   */
  subscribeToUserMediaLiveChanges(
    userId: string,
    mediaId: string,
    onChange: (change: { type: RemoteDocumentChangeType; media: UserMediaEntity }) => void,
  ): Unsubscribe {
    return firestoreService.subscribeToUserMediaById(
      userId,
      mediaId,
      (changes) => {
        for (const c of changes) {
          onChange({ type: c.type, media: UserMediaRemapper.toEntity(c.data) });
        }
      },
      (error) => appLogger.error('subscribeToUserMediaLiveChanges error', 'UserMediaService.subscribeToUserMediaLiveChanges', { error: error }),
    );
  }

  /**
   * Fetch media documents whose `path` matches any value in `paths`, remapped
   * to UserMediaEntity instances.
   *
   * Mirrors the Firestore query portion of Flutter's
   * content_data_source.dart:getUnlockedContent (lines 362-400). Blocked /
   * hidden / usersBlockedMe filtering and path-order preservation are
   * intentionally NOT handled here — those are use-case concerns
   * (see fetchUnlockedVideosPageUseCase).
   *
   * Firestore's `in` operator caps at 10 values; the caller is responsible
   * for ensuring `paths.length <= 10`.
   */
  async getMediasByPaths(paths: string[]): Promise<UserMediaEntity[]> {
    if (paths.length === 0) {
      return [];
    }

    if (paths.length > SERVER_FETCH_CONSTANTS.FIRESTORE_WHERE_IN_MAX_VALUES) {
      const error = new Error(
        `getMediasByPaths received ${paths.length} paths; Firestore "in" supports at most ${SERVER_FETCH_CONSTANTS.FIRESTORE_WHERE_IN_MAX_VALUES}. Chunk upstream.`,
      );
      appLogger.error(
        'getMediasByPaths called with too many paths',
        'FirestoreService.getMediasByPaths',
        { context: { pathCount: paths.length, max: SERVER_FETCH_CONSTANTS.FIRESTORE_WHERE_IN_MAX_VALUES }, error },
      );
      throw error;
    }

    const rawMedias = await firestoreService.getMediasByPaths(paths);
    return rawMedias.map((data) => UserMediaRemapper.toEntity(data));
  }

  /**
   * Fetch the current user's medias that are eligible to be linked to another
   * piece of content (mirrors Flutter's getMediasToLink).
   * Remaps raw DTOs to domain entities.
   */
  async fetchLinkableMedias(
    userId: string,
    excludeMediaId: string,
    limitCount: number,
    cursor?: PageCursor,
  ): Promise<{ medias: UserMediaEntity[]; hasMore: boolean; cursor?: PageCursor }> {
    const result = await firestoreService.getMediasToLink(
      userId,
      excludeMediaId,
      limitCount,
      cursor,
    );

    return {
      medias: result.items.map(item => UserMediaRemapper.toEntity(item)),
      hasMore: result.hasMore,
      cursor: result.cursor,
    };
  }
}

// Export singleton instance for convenience
export const userMediaService = UserMediaService.getInstance();